import { useCallback, useEffect, useMemo, type FC } from "react";
import {
  DatePicker as AntDatePicker,
  TimePicker as AntTimePicker,
  message,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { ToggleSwitch } from "../../../shared";
import {
  buildMonthlyAutoStartSchedule,
  type MonthlyAutoStartAnchor,
  type MonthlyScheduleValues,
} from "../monthlyScheduleData";

import styles from "./MonthlySchedule.module.css";

export type MonthlyScheduleProps = {
  values: MonthlyScheduleValues;
  /** Receives the full snapshot after each field change so the parent can validate. */
  onValuesChange: (values: MonthlyScheduleValues) => void;
  /** The currently ACTIVE monthly challenge the new one auto-starts after (see
   *  `resolveMonthlyAutoStartAnchor`). When auto-start is toggled on, the start
   *  date+time are auto-filled to one minute after this challenge's end (and
   *  locked), and the end is pre-filled three days later (but stays editable).
   *  `null` / omitted means there's nothing to chain after, so toggling on is
   *  rejected with a message. */
  autoStartAnchor?: MonthlyAutoStartAnchor | null;
  /** `data-testid` prefix for the rendered controls. */
  testIdPrefix?: string;
};

const DEFAULT_TEST_ID_PREFIX = "monthly-challenge";

/**
 * Create Monthly Challenge — Step 3 (Schedule).
 *
 * Controlled/presentational: the parent (`MonthlyChallenge` / `MonthlyDetails`)
 * owns the canonical `schedule` state and validates it. Renders the auto-start
 * toggle row and the 2×2 start/end date-time grid from the Figma.
 *
 * FE-2374 aligns the behaviour with the Season wizard: when auto-start is on the
 * start date+time auto-fill from the active monthly challenge's end (one minute
 * later) and lock; the end date+time pre-fill three days out but stay editable.
 * The "Select Season" dropdown was removed — the anchor is resolved from the
 * active challenge, not chosen by hand.
 */
const MonthlySchedule: FC<MonthlyScheduleProps> = ({
  values,
  onValuesChange,
  autoStartAnchor = null,
  testIdPrefix = DEFAULT_TEST_ID_PREFIX,
}) => {
  const update = useCallback(
    (patch: Partial<MonthlyScheduleValues>) => {
      onValuesChange({ ...values, ...patch });
    },
    [onValuesChange, values],
  );

  // Start (and the default end) the auto-started challenge inherits from the
  // active challenge's end instant. Memoised so the toggle handler and the
  // late-load effect below derive the same window.
  const autoStartFill = useMemo(
    () => buildMonthlyAutoStartSchedule(autoStartAnchor?.end ?? null),
    [autoStartAnchor],
  );

  const autoStartActive = !!values.autoStartAfter;

  const handleAutoStartToggle = useCallback(
    (next: boolean) => {
      if (next) {
        // Auto-start chains the new challenge after the active one. With no
        // active challenge there is nothing to chain after, so reject the toggle
        // (it stays off) and tell the admin why instead of silently enabling an
        // unschedulable state.
        if (!autoStartAnchor || !autoStartFill.startDate) {
          message.error(AppStrings.GAME_PASS_MONTHLY_SCHEDULE_NO_ACTIVE_CHALLENGE);
          return;
        }
        // Toggling auto-start on is a deliberate "use the computed schedule"
        // action, so overwrite both the locked start (active end + 1 min) and
        // the default end (+3 days) — even over any placeholder window a draft
        // was seeded with. The end stays editable afterwards.
        update({
          autoStartAfter: true,
          startDate: autoStartFill.startDate,
          startTime: autoStartFill.startTime,
          endDate: autoStartFill.endDate,
          endTime: autoStartFill.endTime,
        });
        return;
      }
      // Turning auto-start off releases the locked start for manual entry.
      update({ autoStartAfter: false });
    },
    [autoStartAnchor, autoStartFill, update],
  );

  // Backfill the locked start and default end when the anchor resolves *after*
  // auto-start was switched on — e.g. the admin toggled it before the campaigns
  // list finished loading. Guarded on an empty `startDate` so it fires once and
  // never overwrites an end the admin has since edited.
  useEffect(() => {
    if (!autoStartActive || !autoStartFill.startDate || values.startDate) {
      return;
    }
    update({
      startDate: autoStartFill.startDate,
      startTime: autoStartFill.startTime,
      endDate: values.endDate ?? autoStartFill.endDate,
      endTime: values.endTime ?? autoStartFill.endTime,
    });
  }, [
    autoStartActive,
    autoStartFill,
    update,
    values.startDate,
    values.endDate,
    values.endTime,
  ]);

  return (
    <section
      className={styles.container}
      data-testid={`${testIdPrefix}-schedule`}
      aria-labelledby={`${testIdPrefix}-schedule-title`}
    >
      <header className={styles.intro}>
        <h2 id={`${testIdPrefix}-schedule-title`} className={styles.title}>
          {AppStrings.SCHEDULE_SEASON}
        </h2>
        <p className={styles.description}>
          {AppStrings.MONTHLY_CHALLENGE_SCHEDULE_DESCRIPTION}
        </p>
      </header>

      <div className={styles.autoStartRow}>
        <label
          className={styles.toggleLabel}
          data-testid={`${testIdPrefix}-auto-start-toggle`}
        >
          <ToggleSwitch
            value={autoStartActive}
            onChange={handleAutoStartToggle}
            tone="info"
          />
          <span>{AppStrings.MONTHLY_CHALLENGE_SCHEDULE_AUTO_START}</span>
        </label>
      </div>

      <div className={styles.grid}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {`${AppStrings.START_DATE}*`}
            <InfoCircleOutlined
              className={styles.fieldInfoIcon}
              aria-hidden="true"
            />
          </label>
          <AntDatePicker
            value={values.startDate ?? null}
            onChange={(next: Dayjs | null) =>
              update({ startDate: next ?? undefined })
            }
            disabled={autoStartActive}
            placeholder="mm/dd/yyyy"
            format="MM/DD/YYYY"
            className={`${styles.input}${
              autoStartActive ? ` ${styles.lockedInput}` : ""
            }`}
            data-testid={`${testIdPrefix}-start-date`}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {`${AppStrings.START_TIME}*`}
            <InfoCircleOutlined
              className={styles.fieldInfoIcon}
              aria-hidden="true"
            />
          </label>
          <AntTimePicker
            value={values.startTime ?? null}
            onChange={(next: Dayjs | null) =>
              update({ startTime: next ?? undefined })
            }
            disabled={autoStartActive}
            placeholder="12:00 am"
            format="hh:mm a"
            use12Hours
            className={`${styles.input}${
              autoStartActive ? ` ${styles.lockedInput}` : ""
            }`}
            data-testid={`${testIdPrefix}-start-time`}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {`${AppStrings.END_DATE}*`}
            <InfoCircleOutlined
              className={styles.fieldInfoIcon}
              aria-hidden="true"
            />
          </label>
          <AntDatePicker
            value={values.endDate ?? null}
            onChange={(next: Dayjs | null) =>
              update({ endDate: next ?? undefined })
            }
            placeholder="mm/dd/yyyy"
            format="MM/DD/YYYY"
            className={styles.input}
            data-testid={`${testIdPrefix}-end-date`}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>
            {`${AppStrings.END_TIME}*`}
            <InfoCircleOutlined
              className={styles.fieldInfoIcon}
              aria-hidden="true"
            />
          </label>
          <AntTimePicker
            value={values.endTime ?? null}
            onChange={(next: Dayjs | null) =>
              update({ endTime: next ?? undefined })
            }
            placeholder="11:59 pm"
            format="hh:mm a"
            use12Hours
            className={styles.input}
            data-testid={`${testIdPrefix}-end-time`}
          />
        </div>
      </div>
    </section>
  );
};

export default MonthlySchedule;
